import React, { useEffect, useRef, useState } from 'react';
import {
  Bot,
  Mic,
  Minus,
  Paperclip,
  Plus,
  Send,
  User,
  Video,
  X,
} from 'lucide-react';
import { buildApiUrl } from '../config/appConfig';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  attachmentType?: 'image' | 'file' | 'audio';
  attachmentUrl?: string;
}

interface Particle {
  x: number;
  y: number;
  char: string;
  size: number;
  speed: number;
  direction: number;
}

const ambientCharacters =
  'CalmBalanceFocusRestBreatheSupportReflectRestore'.split('').join('') +
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

const MentalHealthSupport: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draftMessage, setDraftMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);

  useDocumentMetadata(
    'Mental Wellness Support',
    'Chat with the MediInsight AI mental wellness assistant and explore the supportive care experience.'
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    updateCanvasSize();

    particlesRef.current = Array.from({ length: 160 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      char: ambientCharacters[Math.floor(Math.random() * ambientCharacters.length)],
      size: 18 + Math.random() * 18,
      speed: 0.15 + Math.random() * 0.4,
      direction: Math.random() * Math.PI * 2,
    }));

    const animate = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = 'rgba(14, 116, 144, 0.15)';

      particlesRef.current.forEach((particle) => {
        context.font = `${particle.size}px Arial`;
        context.fillText(particle.char, particle.x, particle.y);

        particle.x += Math.cos(particle.direction) * particle.speed * animationSpeed;
        particle.y += Math.sin(particle.direction) * particle.speed * animationSpeed;

        if (particle.x < 0 || particle.x > canvas.width) {
          particle.direction = Math.PI - particle.direction;
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.direction = -particle.direction;
        }

        if (Math.random() < 0.0015) {
          particle.direction += (Math.random() - 0.5) * (Math.PI / 6);
        }
      });

      requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener('resize', updateCanvasSize);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [animationSpeed]);

  const sendMessageToBackend = async (message: string) => {
    setIsLoading(true);

    try {
      const response = await fetch(buildApiUrl('/mental-health-chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        throw new Error('Unable to reach the support assistant.');
      }

      const data = await response.json();
      return data.response as string;
    } catch (error) {
      console.error('Error sending message to backend:', error);
      return "I'm sorry, I'm having trouble connecting right now. Please try again later.";
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!draftMessage.trim() && !selectedFile) {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: draftMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    if (selectedFile) {
      userMessage.attachmentType = selectedFile.type.startsWith('image/') ? 'image' : 'file';
      userMessage.attachmentUrl = URL.createObjectURL(selectedFile);
    }

    setMessages((currentMessages) => [...currentMessages, userMessage]);
    setDraftMessage('');
    setSelectedFile(null);

    const assistantResponse = await sendMessageToBackend(draftMessage);

    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: (Date.now() + 1).toString(),
        text: assistantResponse,
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-slate-100">
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />

      <div className="relative z-10 border-b border-cyan-700/30 bg-cyan-800/95 p-4 text-white backdrop-blur">
        <h1 className="text-2xl font-bold">Mental Wellness Support</h1>
        <p className="mt-1 text-sm text-cyan-50">
          Supportive AI conversation space for reflection, grounding, and next-step guidance.
        </p>
        <div className="mt-3 flex items-center">
          <button onClick={() => setAnimationSpeed((speed) => Math.max(0.1, speed - 0.1))} className="mr-2 rounded bg-cyan-900 p-1">
            <Minus size={16} />
          </button>
          <span>Animation Speed: {animationSpeed.toFixed(1)}x</span>
          <button onClick={() => setAnimationSpeed((speed) => Math.min(2, speed + 0.1))} className="ml-2 rounded bg-cyan-900 p-1">
            <Plus size={16} />
          </button>
        </div>
      </div>

      <div className="relative z-10 flex-grow overflow-y-auto p-4">
        {messages.length === 0 && (
          <div className="mx-auto mt-10 max-w-2xl rounded-3xl bg-white/85 p-8 text-center shadow-sm backdrop-blur">
            <h2 className="text-xl font-semibold text-slate-900">Start a supportive conversation</h2>
            <p className="mt-3 text-slate-600">
              Ask about stress, burnout, sleep disruption, or ways to reset during a demanding day.
            </p>
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={message.id}
            className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
            style={{ animationDelay: `${index * 0.08}s` }}
          >
            <div className={`flex items-end ${message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div
                className={`rounded-full p-2 ${
                  message.sender === 'user' ? 'bg-cyan-600' : 'bg-white'
                }`}
              >
                {message.sender === 'user' ? (
                  <User size={24} className="text-white" />
                ) : (
                  <Bot size={24} className="text-slate-600" />
                )}
              </div>
              <div
                className={`mx-2 max-w-xs rounded-2xl p-4 shadow-sm ${
                  message.sender === 'user'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-slate-800'
                } animate-bounce-in`}
              >
                {message.attachmentType === 'image' && (
                  <img
                    src={message.attachmentUrl}
                    alt="Uploaded"
                    className="mb-2 max-w-xs rounded"
                  />
                )}
                {message.attachmentType === 'file' && (
                  <a
                    href={message.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm underline"
                  >
                    Attached file
                  </a>
                )}
                <div>{message.text}</div>
                <div className="mt-2 text-xs opacity-70">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      <div className="relative z-10 border-t border-slate-200 bg-white/95 p-4 backdrop-blur">
        <div className="mb-2 flex items-center space-x-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="rounded-full bg-slate-200 p-2 transition-colors hover:bg-slate-300"
          >
            <Paperclip size={20} />
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
          <button
            onClick={() => setIsRecording((recording) => !recording)}
            className={`rounded-full p-2 transition-colors ${
              isRecording ? 'bg-rose-500 text-white' : 'bg-slate-200 hover:bg-slate-300'
            }`}
          >
            <Mic size={20} />
          </button>
          <button
            onClick={() => setIsVideoOpen(true)}
            className="rounded-full bg-slate-200 p-2 transition-colors hover:bg-slate-300"
          >
            <Video size={20} />
          </button>
          {selectedFile && (
            <span className="truncate text-sm text-slate-500">{selectedFile.name}</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <textarea
            value={draftMessage}
            onChange={(event) => setDraftMessage(event.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Share what you're feeling or what kind of support would help right now..."
            className="flex-grow rounded-2xl border border-slate-300 p-3 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
            rows={1}
          />
          <button
            onClick={() => void handleSend()}
            disabled={isLoading}
            className={`rounded-full bg-cyan-600 p-3 text-white transition-colors ${
              isLoading ? 'cursor-not-allowed opacity-50' : 'hover:bg-cyan-700'
            }`}
          >
            <Send size={20} />
          </button>
        </div>
      </div>

      {isVideoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-3xl rounded-3xl bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Guided Relaxation Video</h2>
              <button
                onClick={() => setIsVideoOpen(false)}
                className="text-slate-500 transition hover:text-slate-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="aspect-video overflow-hidden rounded-2xl">
              <iframe
                src="https://www.youtube.com/embed/inpok4MKVLM"
                title="Guided relaxation"
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentalHealthSupport;
